/** Copyright (c) 2018 T.Rowe Price. All rights reserved. */
import moment from "moment";
import { concat, forEach, cloneDeep } from 'lodash';

import { RECEIVED_YTD_DATA, ERROR_YTD_DATA } from 'state/Actions';
import { RECEIVED_ACCOUNTS } from 'state/account/AccountBalancesActions';
import { maxAsOfDate, percentChange } from 'services/Calculation';
import { products } from 'services/Products';

export const balancesInitialState = {
  aggregate: 0.0,
  dailyChange: 0.0,
  dailyRate: 0.0,
  monthlyAvailable: false,
  monthlyChange: 0.0,
  monthlyRate: 0.0,
  yearlyAvailable: false,
  yearlyChange: 0.0,
  yearlyRate: 0.0,
  asOfDate: moment.utc().format(),
  beginningTotal: 0.0,
  retirement: {
    label: 'Retirement',
    aggregate: 0.0,
    percent: 0.0,
    products: cloneDeep(products)
  },
  nonRetirement: {
    label: 'Non-Retirement',
    aggregate: 0.0,
    percent: 0.0,
    products: cloneDeep(products)
  }
};

export const accountBalancesReducer = (
  state = balancesInitialState,
  action
) => {
  if (action.type === RECEIVED_ACCOUNTS) {
    // initialize
    let balances = cloneDeep(state);
    balances.aggregate = 0.0;
    balances.dailyChange = 0.0;
    for (let key in balances.retirement.products.types) {
      balances.retirement.aggregate = 0.0;
      balances.retirement.products.types[key].balance = 0.0;
      balances.retirement.products.types[key].userAccountNumbers = [];
    }
    for (let key in balances.nonRetirement.products.types) {
      balances.nonRetirement.aggregate = 0.0;
      balances.nonRetirement.products.types[key].balance = 0.0;
      balances.nonRetirement.products.types[key].userAccountNumbers = [];
    }

    // filter
    const nonPurgedAccounts = action.accounts.filter((a) => !a.purgedAccount);
    const visibleNonPurgedAccounts = nonPurgedAccounts.filter(
      (a) => !a.hiddenAccount
    );
    const hiddenAccounts = nonPurgedAccounts.filter(
      (account) => account.hiddenAccount
    );
    const zeroBalanceHiddenAccounts = hiddenAccounts.filter(
      (account) => account.balanceValue === 0.0
    );
    const accounts = concat(
      visibleNonPurgedAccounts,
      zeroBalanceHiddenAccounts
    );

    // populate
    let aggregatePrevious = 0.0;
    balances.asOfDate = maxAsOfDate(accounts);
    accounts.map((account) => {
      balances.aggregate += account.balanceValue;
      aggregatePrevious += account.previousBalanceValue;
      balances.dailyChange +=
        account.balanceValue - account.previousBalanceValue;
      if (account.retirementAccount) {
        let purpose = balances.retirement;
        purpose.aggregate += account.balanceValue;
        let product = purpose.products.byAccount[account.productType];
        if (product) {
          product.balance += account.balanceValue;
          product.userAccountNumbers.push(account.productType);
        }
      } else {
        let purpose = balances.nonRetirement;
        purpose.aggregate += account.balanceValue;
        let product = purpose.products.byAccount[account.productType];
        if (product) {
          product.balance += account.balanceValue;
          product.userAccountNumbers.push(account.productType);
        }
      }
    });

    balances.dailyRate = percentChange(balances.aggregate, aggregatePrevious);

    const rp = balances.retirement;
    rp.percent = balances.aggregate ? rp.aggregate / balances.aggregate : 0.0;
    forEach(rp.products.types, function (type) {
      type.percent = rp.aggregate ? type.balance / rp.aggregate : 0.0;
    });
    const nrp = balances.nonRetirement;
    nrp.percent = balances.aggregate ? nrp.aggregate / balances.aggregate : 0.0;
    forEach(nrp.products.types, function (type) {
      type.percent = nrp.aggregate ? type.balance / nrp.aggregate : 0.0;
    });
    return balances;
  }

  if (action.type === ERROR_YTD_DATA) {
    let balances = cloneDeep(state);
    balances.yearlyAvailable = false;
    return balances;
  }

  if (action.type === RECEIVED_YTD_DATA) {
    return getYTDBalance(state, action);
  }
  return state;
};

export const getYTDBalance = (state, action) => {
  let balances = cloneDeep(state);
  const yearlyAccounts = action.ytdData;
  let beginningTotal = 0;
  let todaysTotal = 0;
  /* we have these values at the account level in the response from the service they need to be summed to come up
   with balance bar totals for the year
   */
  if (!yearlyAccounts) {
    return balances;
  }
  if (yearlyAccounts && typeof yearlyAccounts.forEach == 'function') {
    yearlyAccounts.forEach((account) => {
      if (account) {
        if (account.beginningMarketValue) {
          beginningTotal += account.beginningMarketValue;
        }
        if (account.endingMarketValue) {
          todaysTotal += account.endingMarketValue;
        }
      }
    });
  }
  const yearlyChange = todaysTotal - beginningTotal;
  const yearlyRate = yearlyChange / beginningTotal;
  balances.yearlyChange = yearlyChange;
  balances.yearlyRate = Number(beginningTotal) === 0 ? 1 : yearlyRate;
  balances.beginningTotal = beginningTotal;
  if (yearlyChange && yearlyRate) {
    balances.yearlyAvailable = true;
  }
  return balances;
};

accountBalancesReducer.priority = 17;
